import { AdminRole } from "@repo/api-dtos"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { format, formatDistanceToNow } from "date-fns"
import {
	ArrowLeft,
	CalendarClock,
	Gift,
	History,
	Pencil,
	UserPlus,
} from "lucide-react"
import * as React from "react"
import {
	DISCOUNT_FUNDING_LABELS,
	describeBuyerPromotionBenefit,
	needsShopToApply,
} from "@/components/buyer-promotions/buyer-promotion-form.helpers"
import {
	DetailPanel,
	DetailPanelSection,
	KeyValueItem,
	KeyValueList,
	Section,
	Stat,
	StatStrip,
} from "@/components/layout"
import { BACK_LINK_CLASS } from "@/components/promotions/back-link"
import {
	DetailColumn,
	DetailLayout,
	DetailPage,
	DetailPageHeader,
} from "@/components/promotions/detail-page"
import {
	countryName,
	describeTranslatedName,
	formatBps,
	formatCents,
	formatDateTime,
	formatWindow,
	TRANSLATED_NAME_LOCALES,
} from "@/components/promotions/promotion-helpers"
import { PromotionStatusBadge } from "@/components/promotions/promotion-status-badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusTag } from "@/components/ui/status"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip"
import { AddShopsDialog } from "@/features/buyer-promotions/AddShopsDialog"
import { BuyerPromotionShopsGrid } from "@/features/buyer-promotions/BuyerPromotionShopsGrid"
import { OptInLinkCard } from "@/features/buyer-promotions/OptInLinkCard"
import { useAdminRoles } from "@/hooks/use-admin-roles"
import { createTabSearch } from "@/hooks/use-url-tab"
import {
	useBuyerPromotion,
	useSetBuyerPromotionActive,
} from "@/queries/marketplace"
import type { AdminBuyerPromotion } from "@/types/promotions.types"

const TABS = ["overview", "shops"] as const
type BuyerPromotionTab = (typeof TABS)[number]

export const Route = createFileRoute("/buyer-promotions/$promotionId/")({
	component: BuyerPromotionDetailPage,
	validateSearch: createTabSearch(TABS),
})

function BenefitSection({ promotion }: { promotion: AdminBuyerPromotion }) {
	const fundedByCardNexus = promotion.discountFundedBy === "cardnexus"

	return (
		<DetailPanel title="Benefit" icon={Gift}>
			<DetailPanelSection label="Fee and discount">
				<KeyValueList>
					<KeyValueItem label="Buyer fee">
						{promotion.waiveBuyerFee
							? "Not charged (CardNexus covers it)"
							: "Charged as usual"}
					</KeyValueItem>
					<KeyValueItem label="Discount">
						{promotion.discountBps === null ? (
							<span className="text-meta">None</span>
						) : (
							formatBps(promotion.discountBps)
						)}
					</KeyValueItem>
					{promotion.discountBps !== null && (
						<>
							<KeyValueItem label="Discount paid by">
								{DISCOUNT_FUNDING_LABELS[promotion.discountFundedBy]}
							</KeyValueItem>
							{promotion.shopShareBps !== null && (
								<KeyValueItem label="Shop's share of the discount">
									{formatBps(promotion.shopShareBps)}
								</KeyValueItem>
							)}
							{fundedByCardNexus && (
								<KeyValueItem label="Maximum discount per order">
									{promotion.maxDiscountAmounts.length === 0
										? "No maximum"
										: promotion.maxDiscountAmounts
												.map((cap) => formatCents(cap.amount, cap.currency))
												.join(", ")}
								</KeyValueItem>
							)}
						</>
					)}
				</KeyValueList>
			</DetailPanelSection>
			<DetailPanelSection label="Where it applies">
				<KeyValueList>
					<KeyValueItem label="Shops">
						{needsShopToApply(promotion)
							? "None yet (not applied until a shop is added)"
							: promotion.restrictedToShops
								? `Only the ${promotion.shops.length} shop${promotion.shops.length === 1 ? "" : "s"} listed`
								: "Any shop"}
					</KeyValueItem>
					<KeyValueItem label="Delivery countries">
						{promotion.countries.length === 0
							? "All countries"
							: promotion.countries.map(countryName).join(", ")}
					</KeyValueItem>
				</KeyValueList>
			</DetailPanelSection>
		</DetailPanel>
	)
}

function PromotionSummaryPanel({
	promotion,
}: {
	promotion: AdminBuyerPromotion
}) {
	return (
		<DetailColumn>
			<DetailPanel title="Dates" icon={CalendarClock}>
				<DetailPanelSection>
					<KeyValueList>
						<KeyValueItem label="Starts">
							{formatDateTime(promotion.startsAt)}
						</KeyValueItem>
						<KeyValueItem label="Ends">
							{formatDateTime(promotion.endsAt)}
						</KeyValueItem>
					</KeyValueList>
				</DetailPanelSection>
			</DetailPanel>
			<DetailPanel title="Record" icon={History}>
				<DetailPanelSection>
					<KeyValueList>
						<KeyValueItem label="Created">
							{format(new Date(promotion.createdAt), "PPP p")}
						</KeyValueItem>
						<KeyValueItem label="Last updated">
							{formatDistanceToNow(new Date(promotion.updatedAt), {
								addSuffix: true,
							})}
						</KeyValueItem>
						<KeyValueItem label="Created by">
							{promotion.createdByUsername ? (
								<span>@{promotion.createdByUsername}</span>
							) : (
								<span className="font-mono">
									{promotion.createdBy ?? "None"}
								</span>
							)}
						</KeyValueItem>
					</KeyValueList>
				</DetailPanelSection>
			</DetailPanel>
		</DetailColumn>
	)
}

function BuyerPromotionDetailPage() {
	const { promotionId } = Route.useParams()
	const search = Route.useSearch()
	const navigate = useNavigate()
	const { promotion, isLoading, error } = useBuyerPromotion(promotionId)
	const setActiveMutation = useSetBuyerPromotionActive()
	const { hasRole } = useAdminRoles()
	const canOperate = hasRole(AdminRole.MarketplaceCoupons)
	const [addShopsOpen, setAddShopsOpen] = React.useState(false)

	if (isLoading) {
		return (
			<DetailPage>
				<Skeleton className="h-8 w-64" />
				<Skeleton className="h-[78px]" />
				<DetailLayout>
					<div className="flex flex-col gap-3">
						<Skeleton className="h-9" />
						<Skeleton className="h-9" />
						<Skeleton className="h-9" />
					</div>
					<Skeleton className="h-64" />
				</DetailLayout>
			</DetailPage>
		)
	}

	if (error || !promotion) {
		return (
			<DetailPage className="items-start gap-3">
				<p className="text-bad">
					{error?.message || "The buyer promotion could not be found."}
				</p>
				<Button variant="outline" asChild>
					<Link to="/buyer-promotions">
						<ArrowLeft />
						Back to buyer promotions
					</Link>
				</Button>
			</DetailPage>
		)
	}

	const activeTab: BuyerPromotionTab = search.tab ?? "overview"
	const setActiveTab = (tab: string) => {
		navigate({
			to: "/buyer-promotions/$promotionId",
			params: { promotionId },
			search: tab === "shops" ? { tab: "shops" } : {},
			replace: true,
		})
	}
	const shopCount = promotion.shops.length

	return (
		<DetailPage>
			<DetailPageHeader
				back={
					<Link to="/buyer-promotions" className={BACK_LINK_CLASS}>
						<ArrowLeft />
						Buyer promotions
					</Link>
				}
				title={promotion.name}
				badges={
					<>
						<code className="font-mono text-sm break-all text-muted-foreground">
							{promotion.key}
						</code>
						<PromotionStatusBadge status={promotion.status} />
						{needsShopToApply(promotion) && (
							<StatusTag tone="warn">
								Not applied until a shop is added
							</StatusTag>
						)}
					</>
				}
				description={
					<span className="flex flex-col gap-0.5">
						<span>Buyers see: {promotion.publicNames.en}</span>
						{TRANSLATED_NAME_LOCALES.map((locale) => (
							<span key={locale}>
								{describeTranslatedName(promotion.publicNames, locale)}
							</span>
						))}
					</span>
				}
				actions={
					<>
						<Tooltip>
							<TooltipTrigger asChild>
								<div className="flex items-center gap-2 pr-2">
									<Label htmlFor="buyer-promotion-active">Switched on</Label>
									<Switch
										id="buyer-promotion-active"
										checked={promotion.isActive}
										disabled={!canOperate || setActiveMutation.isPending}
										onCheckedChange={(checked) =>
											setActiveMutation.mutate({
												id: promotion.id,
												isActive: checked,
											})
										}
									/>
								</div>
							</TooltipTrigger>
							<TooltipContent className="max-w-xs">
								While paused, carts no longer get the promotion. Orders already
								paid keep it.
							</TooltipContent>
						</Tooltip>
						{canOperate && (
							<Button variant="outline" asChild>
								<Link
									to="/buyer-promotions/$promotionId/edit"
									params={{ promotionId: promotion.id }}
								>
									<Pencil />
									Edit
								</Link>
							</Button>
						)}
					</>
				}
			/>

			<StatStrip>
				<Stat
					label="Uses"
					value={promotion.usageCount.toLocaleString()}
					detail="Paid checkouts with this promotion"
					muted={promotion.usageCount === 0}
				/>
				<Stat
					label="Benefit"
					value={
						<span className="text-base">
							{describeBuyerPromotionBenefit(promotion)}
						</span>
					}
				/>
				<Stat
					label="Shops"
					value={
						shopCount > 0
							? shopCount.toLocaleString()
							: needsShopToApply(promotion)
								? "None yet"
								: "Any shop"
					}
					muted={shopCount === 0}
				/>
				<Stat
					label="Dates"
					value={
						<span className="text-base">
							{formatWindow(promotion.startsAt, promotion.endsAt)}
						</span>
					}
				/>
			</StatStrip>

			<Tabs value={activeTab} onValueChange={setActiveTab} className="gap-6">
				<TabsList variant="line">
					<TabsTrigger value="overview">Overview</TabsTrigger>
					<TabsTrigger value="shops" className="gap-2">
						Shops
						<span className="text-meta tabular-nums">
							{shopCount.toLocaleString()}
						</span>
					</TabsTrigger>
				</TabsList>

				<TabsContent value="overview" className="mt-0">
					<DetailLayout>
						<BenefitSection promotion={promotion} />
						<PromotionSummaryPanel promotion={promotion} />
					</DetailLayout>
				</TabsContent>

				<TabsContent value="shops" className="mt-0">
					<DetailColumn>
						{promotion.discountBps !== null &&
							promotion.discountFundedBy !== "cardnexus" && (
								<OptInLinkCard promotion={promotion} canOperate={canOperate} />
							)}
						<Section
							label="Shops"
							action={
								canOperate && (
									<Button
										variant="outline"
										size="sm"
										onClick={() => setAddShopsOpen(true)}
									>
										<UserPlus />
										Add shops
									</Button>
								)
							}
						>
							<p className="text-muted-foreground">
								{needsShopToApply(promotion)
									? "No shops yet: the shop pays all or part of the discount, so the promotion is not applied until one is added."
									: shopCount === 0
										? "No shops: every shop's items get the promotion."
										: "Only items from these shops get the promotion."}
							</p>
							<BuyerPromotionShopsGrid
								promotion={promotion}
								canOperate={canOperate}
							/>
						</Section>
					</DetailColumn>
				</TabsContent>
			</Tabs>

			<AddShopsDialog
				promotionId={promotion.id}
				promotionName={promotion.name}
				open={addShopsOpen}
				onOpenChange={setAddShopsOpen}
			/>
		</DetailPage>
	)
}
